import { zodResolver } from "@hookform/resolvers/zod"
import { StatusCodes } from "http-status-codes"
import { useCallback, useContext, useEffect, useMemo } from "react"
import { Controller, useForm } from "react-hook-form"
import { useParams } from "react-router-dom"
import { toast } from "react-toastify"
import { useTrainingCourses } from "../../../../../../hooks/useTrainingCourses"
import { digitalManagerApi } from "../../../../../../services/api"
import {
  DiMaDetailsContent,
  DiMaDetailsContentProps,
} from "../../../../../components/DiMaDetailsContent"
import { DiMaProgress } from "../../../../../components/DiMaProgress"
import {
  createTrainingCourseValidationSchema,
  useTrainingCourseFieldRenderers,
} from "../../../../../components/Helpers"
import { UserContext } from "../../../../../context/UserContext"
import {
  AccessLevel,
  DimaFeatureShortNameEnum,
  hasUserAccessToApplicationFeature,
} from "../../../../../models/Authorization"
import { TrainingGet } from "../../../../../models/Training"
import {
  TrainingCourseGet,
  TrainingCoursePut,
} from "../../../../../models/TrainingCourse"
import { getApiErrorMessage } from "../../../../../utils/getErrorMessage"

export function TrainingCourseDetails(props: {
  readonly training: TrainingGet
  readonly trainingCourse: TrainingCourseGet
  readonly setTrainingCourse: (updatedTrainingCourse: TrainingCourseGet) => void
}) {
  const { user, isLoadingUser } = useContext(UserContext)
  const { training, trainingCourse, setTrainingCourse } = props
  const { trainingId } = useParams()
  const { trainingCourses } = useTrainingCourses(trainingId)

  const validationSchema = useMemo(
    () =>
      createTrainingCourseValidationSchema(
        trainingCourses.filter((course) => course.id !== trainingCourse.id)
      ),
    [trainingCourses, trainingCourse.id]
  )

  const formMethods = useForm<TrainingCoursePut>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
  })

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = formMethods

  useEffect(() => {
    reset({
      name: trainingCourse.name,
      title: trainingCourse.title,
      description: trainingCourse.description,
      type: trainingCourse.type,
      contentUrl: trainingCourse.contentUrl,
      duration: trainingCourse.duration,
      disclaimer: trainingCourse.disclaimer ?? "",
      position: trainingCourse.position,
    })
  }, [trainingCourse, reset])

  const onSubmit = useCallback(
    (values: TrainingCoursePut) =>
      digitalManagerApi
        .put<TrainingCourseGet>(`/trainings/${trainingId}/courses/${trainingCourse.id}`, {
          name: values.name,
          title: values.title,
          description: values.description,
          type: values.type,
          contentUrl: values.contentUrl,
          duration: values.duration,
          disclaimer: values.disclaimer ? values.disclaimer : null,
          position: values.position,
        })
        .then((res) => {
          if (res.status === StatusCodes.OK) {
            toast.success("Course updated")
            setTrainingCourse(res.data)
          }
        })
        .catch((err) => {
          toast.error(getApiErrorMessage(err, "Cannot update course"))
          throw new Error("Cannot update course")
        }),
    [trainingId, trainingCourse.id, setTrainingCourse]
  )

  const { renderTextField, renderNumberField, renderTypeSelect } =
    useTrainingCourseFieldRenderers({ errors })

  if (isLoadingUser || !user) {
    return <DiMaProgress />
  }

  const detailsContentProps: DiMaDetailsContentProps<TrainingCoursePut> = {
    formMethods,
    inlineEdit: true,
    canWrite: hasUserAccessToApplicationFeature(
      user.authorizations.applications,
      training.application.id,
      DimaFeatureShortNameEnum.TM,
      AccessLevel.Write
    ),
    listItems: [
      {
        id: 0,
        label: "Training",
        displayItem: <div className="title">{trainingCourse.training?.name}</div>,
      },
      {
        id: 1,
        label: "Name",
        displayItem: <div className="title">{trainingCourse.name}</div>,
        editItem: <Controller name="name" control={control} render={renderTextField} />,
      },
      {
        id: 2,
        label: "Title",
        displayItem: <div className="title">{trainingCourse.title}</div>,
        editItem: <Controller name="title" control={control} render={renderTextField} />,
      },
      {
        id: 3,
        label: "Description",
        displayItem: <div className="title">{trainingCourse.description}</div>,
        editItem: (
          <Controller name="description" control={control} render={renderTextField} />
        ),
      },
      {
        id: 4,
        label: "Type",
        displayItem: <div className="title">{trainingCourse.type}</div>,
        editItem: <Controller name="type" control={control} render={renderTypeSelect} />,
      },
      {
        id: 5,
        label: "Content URL",
        displayItem: <div className="title">{trainingCourse.contentUrl}</div>,
        editItem: (
          <Controller name="contentUrl" control={control} render={renderTextField} />
        ),
      },
      {
        id: 6,
        label: "Duration",
        displayItem: <div className="title">{trainingCourse.duration}</div>,
        editItem: (
          <Controller name="duration" control={control} render={renderTextField} />
        ),
      },
      {
        id: 7,
        label: "Disclaimer",
        displayItem: <div className="title">{trainingCourse.disclaimer ?? ""}</div>,
        editItem: (
          <Controller name="disclaimer" control={control} render={renderTextField} />
        ),
      },
      {
        id: 8,
        label: "Position",
        displayItem: <div className="title">{trainingCourse.position}</div>,
        editItem: (
          <Controller name="position" control={control} render={renderNumberField} />
        ),
      },
    ],
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DiMaDetailsContent {...detailsContentProps} onSubmit={onSubmit} />
    </form>
  )
}
